// Optional, opt-in layout overrides for the PDF report.
//
// Defaults are the production-tuned values from `designTokens.page` and the
// `PAGE_CONTENT_HEIGHT` / `MODULE_GAP` constants in ReportDocument. When all
// override fields are undefined, every consumer falls back to those defaults
// and code paths are byte-identical to the un-overridden behaviour — this is
// intentional so the feature can be enabled for ad-hoc testing without risking
// the production layout that has been tuned across many iterations.
//
// Bounds match what react-pdf can render safely without breaking the running
// header/footer absolute positioning or pushing the cover off the page.

import React, { createContext, useContext } from 'react';
import { page as pageDefaults } from './designTokens';

// Paper presets supported by @react-pdf/renderer. Numeric pairs let advanced
// callers slot in a custom size, but the drawer UI only exposes the named
// presets so the bounds stay enforceable.
export type PdfPaperSize = 'A4' | 'A3' | 'LETTER' | 'LEGAL' | 'TABLOID';

export const PAPER_DIMENSIONS: Record<PdfPaperSize, { width: number; height: number }> = {
    A4: { width: 595.28, height: 841.89 },
    A3: { width: 841.89, height: 1190.55 },
    LETTER: { width: 612, height: 792 },
    LEGAL: { width: 612, height: 1008 },
    TABLOID: { width: 792, height: 1224 },
};

export type PdfLayoutOverrides = {
    paperSize?: PdfPaperSize;
    paddingTop?: number;
    paddingRight?: number;
    paddingBottom?: number;
    paddingLeft?: number;
    /** Inter-module gap inside packIntoPages. Lower = more modules per page. */
    moduleGap?: number;
};

export type EffectivePdfLayout = {
    paperSize: PdfPaperSize;
    pageWidth: number;
    pageHeight: number;
    paddingTop: number;
    paddingRight: number;
    paddingBottom: number;
    paddingLeft: number;
    headerOffset: number;
    footerOffset: number;
    moduleGap: number;
    contentHeight: number;
};

// Hard bounds so a fat-fingered input can't push the header into the title or
// drop content below the footer rule. Header/footer offsets are tied to
// padding (header sits 1/3 of the way into the top padding) so the safety
// zone scales with whatever padding the user picks.
export const LAYOUT_BOUNDS = {
    padding: { min: 16, max: 100 },
    moduleGap: { min: 0, max: 60 },
} as const;

const clamp = (n: number, min: number, max: number): number => Math.min(max, Math.max(min, n));

// Module gap and content-height safety buffer must match what the existing
// packIntoPages uses with default tokens — bake those defaults in here so
// the override path produces identical numbers when nothing is overridden.
export const DEFAULT_MODULE_GAP = 14;
export const DEFAULT_CONTENT_HEIGHT_BUFFER = 24; // (paddingTop+paddingBottom) − usable ≈ 24pt safety

export const resolveLayout = (overrides?: PdfLayoutOverrides): EffectivePdfLayout => {
    const paperSize = overrides?.paperSize ?? 'A4';
    const dims = PAPER_DIMENSIONS[paperSize];

    const paddingTop = clamp(overrides?.paddingTop ?? pageDefaults.paddingTop, LAYOUT_BOUNDS.padding.min, LAYOUT_BOUNDS.padding.max);
    const paddingRight = clamp(overrides?.paddingRight ?? pageDefaults.paddingRight, LAYOUT_BOUNDS.padding.min, LAYOUT_BOUNDS.padding.max);
    const paddingBottom = clamp(
        overrides?.paddingBottom ?? pageDefaults.paddingBottom,
        LAYOUT_BOUNDS.padding.min,
        LAYOUT_BOUNDS.padding.max,
    );
    const paddingLeft = clamp(overrides?.paddingLeft ?? pageDefaults.paddingLeft, LAYOUT_BOUNDS.padding.min, LAYOUT_BOUNDS.padding.max);
    const moduleGap = clamp(overrides?.moduleGap ?? DEFAULT_MODULE_GAP, LAYOUT_BOUNDS.moduleGap.min, LAYOUT_BOUNDS.moduleGap.max);

    // Header sits ~36% of the way into the top padding; footer sits ~43% into
    // the bottom padding. These ratios reproduce the default token values
    // (headerOffset 28.35pt / paddingTop 79.4pt ≈ 0.36; footerOffset 34pt /
    // paddingBottom 79.4pt ≈ 0.43) so when paddings are at default the offsets
    // exactly match the existing tokens.
    const headerOffset = Math.round(paddingTop * 0.357);
    const footerOffset = Math.round(paddingBottom * 0.428);

    const contentHeight = dims.height - paddingTop - paddingBottom - DEFAULT_CONTENT_HEIGHT_BUFFER;

    return {
        paperSize,
        pageWidth: dims.width,
        pageHeight: dims.height,
        paddingTop,
        paddingRight,
        paddingBottom,
        paddingLeft,
        headerOffset,
        footerOffset,
        moduleGap,
        contentHeight,
    };
};

// True only when the user actually picked something — used as a fast-path
// gate so callers can skip the inline-style merge when defaults are in play.
export const hasLayoutOverrides = (overrides?: PdfLayoutOverrides): boolean =>
    !!overrides &&
    (overrides.paperSize != null ||
        overrides.paddingTop != null ||
        overrides.paddingRight != null ||
        overrides.paddingBottom != null ||
        overrides.paddingLeft != null ||
        overrides.moduleGap != null);

// ───────────────────────────────────────────────────────────────────────
// Body-width context — for full-bleed PDF children (e.g. charts) whose
// `<Svg width>` must be a numeric value at render time. Defaulting to the
// historical hardcode (520pt) means any standalone-rendered chart keeps
// its previous size when no provider wraps it. Inside ReportDocument the
// provider yields `pageWidth − paddingLeft − paddingRight − 7pt` (the 7pt
// margin matches the historical gap between A4 usable width 527pt and the
// 520pt the chart used), so on A4 with defaults the chart renders at
// exactly 520pt — identical to today — and on A3 it grows to fill the
// new body width without any other code changes.
// ───────────────────────────────────────────────────────────────────────

const DEFAULT_BODY_WIDTH = 520;
const BODY_WIDTH_EDGE_MARGIN = 7;

export const computeBodyWidth = (overrides?: PdfLayoutOverrides): number => {
    if (!hasLayoutOverrides(overrides)) return DEFAULT_BODY_WIDTH;
    const L = resolveLayout(overrides);
    return Math.max(200, Math.round(L.pageWidth - L.paddingLeft - L.paddingRight - BODY_WIDTH_EDGE_MARGIN));
};

type PdfBodyWidthContextValue = { bodyWidth: number };
const PdfBodyWidthContext = createContext<PdfBodyWidthContextValue>({ bodyWidth: DEFAULT_BODY_WIDTH });

export const PdfBodyWidthProvider: React.FC<{ bodyWidth: number; children: React.ReactNode }> = ({ bodyWidth, children }) =>
    React.createElement(PdfBodyWidthContext.Provider, { value: { bodyWidth } }, children);

export const usePdfBodyWidth = (): number => useContext(PdfBodyWidthContext).bodyWidth;
